
    import { defineComponent, onMounted, ref, toRefs, watch } from 'vue';

    /**
     * PDF.js library
     */
    import { getDocument } from 'pdfjs-dist';
    import {
        DefaultAnnotationLayerFactory,
        DefaultTextLayerFactory,
        PDFFindController,
        PDFLinkService,
        PDFPageView,
        EventBus
    } from 'pdfjs-dist/web/pdf_viewer.js';
    import 'pdfjs-dist/build/pdf.worker.entry';

    function isPDFDocumentLoadingTask (obj: any) {
        return typeof (obj) === 'object' && obj !== null && obj.__PDFDocumentLoadingTask === true;
    }

    function createLoadingTask (src: any) {
        let source;

        if (typeof (src) === 'string') {
            source = {
                url: src
            };
        } else if (typeof (src) === 'object' && src !== null) {
            source = Object.assign({}, src);
        } else {
            throw new TypeError('invalid src type');
        }

        const loadingTask = getDocument(source).promise;
        loadingTask.__PDFDocumentLoadingTask = true;

        return loadingTask;
    }

    export default defineComponent({
        props: {
            src: {
                type: [String, Object],
                default: '',
            },
            page: {
                type: Number,
                default: 1,
            },
            annotation: {
                type: Boolean,
                default: false,
            },
            text: {
                type: Boolean,
                default: true,
            },
            scale: {
                type: Number,
                default: 1
            },
            pdfWidth: {
                type: Number,
                default: 1024
            },
            pdfHeight: {
                type: Number,
                default: 600
            }
        },
        emits: ['num-pages', 'loading', 'link-clicked', 'error'],
        setup (props, {emit}) {
            const { src, page, annotation, text, scale, pdfWidth, pdfHeight } = toRefs(props);

            const internalSrc = ref<any>(null);
            const pdf = ref<any>(null);
            const pdfViewer = ref<any>(null);
            const loading = ref<boolean>(true);

            const pdfLinkService = ref<any>(null);
            const pdfFindController = ref<any>(null);

            const container = ref<any>(null);

            watch(pdf, (currentValue) => {
                const pdfInfo = currentValue.pdfInfo || currentValue._pdfInfo;
                emit('num-pages', pdfInfo.numPages);
            })
            
            watch(page, (currentValue) => {
                renderPage(currentValue)
            })

            /**
             * Init the PDF functions and draw it on promise ok
             */
            function initPdf () {
                if (!isPDFDocumentLoadingTask(src.value)) {
                    internalSrc.value = createLoadingTask(src.value);
                    loading.value = true;
                    emit('loading', loading.value);
                }

                const eventBus = new EventBus();

                pdfLinkService.value = new PDFLinkService({
                    eventBus: eventBus,
                    externalLinkTarget: 2
                });

                pdfFindController.value = new PDFFindController({
                    eventBus: eventBus,
                    linkService: pdfLinkService
                });

                let annotationLayer = null;
                let textLayer = null;

                if (annotation.value) {
                    annotationLayer = new DefaultAnnotationLayerFactory();
                }

                if (text.value) {
                    textLayer = new DefaultTextLayerFactory();
                }

                const srcPromise = internalSrc.value;

                srcPromise.then((pdfDocument: any) => {
                    pdf.value = pdfDocument;
                    return pdfDocument.getPage(page.value);

                }).then((pdfPage: any) => {
                    pdfViewer.value = new PDFPageView({
                        container: container.value,
                        id: page.value,
                        scale: scale.value !== 1 ? scale.value : calculateScaleInit(),
                        defaultViewport: pdfPage.getViewport({
                            scale: scale.value !== 1 ? scale.value : calculateScaleInit()
                        }),
                        eventBus: eventBus,
                        textLayerFactory: textLayer,
                        annotationLayerFactory: annotationLayer,
                    });

                    const viewer = {
                        scrollPageIntoView: (params) => {
                            emit('link-clicked', params);
                        },
                    };

                    pdfViewer.value.setPdfPage(pdfPage);
                    pdfLinkService.value.setDocument(pdf.value);
                    pdfLinkService.value.setViewer(viewer);
                    pdfFindController.value.setDocument(pdf.value);

                    loading.value = false;
                    emit('loading', loading.value);

                    drawViewer();
                }).catch((err) => {
                    console.error(err);
                    emit('error', err);

                    loading.value = false;
                    emit('loading', loading.value);
                })
            }

            /**
             * Draw the PDF viewer and annotations
             */
            function drawViewer () {
                if (pdfViewer.value) {
                    
                    if (annotation.value) {
                        pdfViewer.value.annotationLayer = pdfViewer.value.annotationLayerFactory.createAnnotationLayerBuilder(pdfViewer.value.div, pdfViewer.value.pdfPage);
                        pdfViewer.value.annotationLayer.linkService = pdfLinkService.value;
                    }

                    pdfViewer.value.draw();

                    if (text.value) {
                        pdfViewer.value.textLayer.findController = pdfFindController.value;
                    }

                    loading.value = false;
                    emit('loading', loading.value);
                }
            }

            /**
             * ReScale the viewport
             */
            function reScale () {
                let newScale = calculateScale();

                pdfViewer.value.update(newScale, 0);
            }

            function calculateScale (): number {
                const rotate = 0;
                pdfViewer.value.update(1, rotate);

                const width = container.value.offsetWidth;
                const height = container.value.offsetHeight;

                const viewerWidth = pdfViewer.value ? pdfViewer.value.viewport.width : pdfWidth.value;
                const viewerHeight = pdfViewer.value ? pdfViewer.value.viewport.height : pdfHeight.value;

                const widthScale = width / viewerWidth;
                const heightScale = height / viewerHeight;
    
                return Math.min(widthScale, heightScale);
            }
            
            function calculateScaleInit (): number {
                const width = container.value.offsetWidth;
                const height = container.value.offsetHeight;

                const viewerWidth = pdfViewer.value ? pdfViewer.value.viewport.width : pdfWidth.value;
                const viewerHeight = pdfViewer.value ? pdfViewer.value.viewport.height : pdfHeight.value;

                const widthScale = width / viewerWidth;
                const heightScale = height / viewerHeight;
    
                return Math.min(widthScale, heightScale);
            }

            /**
             * Function to render the page on the viewer
             */
            function renderPage (page: number) {
                if (pdf.value) {
                    pdf.value.getPage(page).then((pdfPage: number) => {
                        pdfViewer.value.setPdfPage(pdfPage);
                        drawViewer();
                    });

                    reScale();
                }
            }

            /**
             * Init the PDF function on mounted and draw it again on resize
             */
            onMounted(() => {
                initPdf();

                window.addEventListener('resize', () => {
                    renderPage(page.value);
                });
            })

            return {
                container, loading
            }
        }
    })
